import { Component, inject, ViewEncapsulation, AfterViewInit, AfterViewChecked, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { commonFunctionService } from '../utils/commonFunctions';
import { ClassLoadTracker, trackService } from './class-load-tracker';
import { LoginService } from '../login/login.service';
import { ClassManageService } from '../classmanage/classmanage.service';
import { GroupService } from '../groups/groups.service';
import { FormValidator } from '../form.validator';
import { Offcanvas } from '../form.offcanvas';
import { ScriptLoader } from '../script-loader';
import { ClassmanageComponent } from '../classmanage/classmanage.component';
import { TvTable } from '../transport-v2/transport-v2.component';

declare var $: any;

/**
 * Class Management v2 — new UI over the exact same behaviour.
 *
 * All state and business logic (categories / classes / sections / subjects /
 * class teachers / timetable configuration + editor / attendance
 * configuration / ordering) is inherited unchanged from ClassmanageComponent,
 * so the two screens cannot drift apart. Only the template and styling are
 * new (same look as Fees Management v2: shared fv2 stylesheet, sliding tabs,
 * stat cards, searchable/paged tables, row entrance). The template keeps
 * every element id, class hook and Bootstrap data-attribute the inherited
 * jQuery code targets (timetable grid, modals, off-canvas panes).
 *
 * The constructor is inherited too (Angular re-uses the parent's DI factory),
 * but component-level providers are not, so they are repeated here.
 */
@Component({
  selector: 'app-classmanage-v2',
  templateUrl: './classmanage-v2.component.html',
  // Same legacy theme list as the old screen (its timetable grid / tag inputs
  // / nestable / timepicker styles are still required), then the fees-v2
  // stylesheet so every shared control looks identical, the old component's
  // own stylesheet (timetable cell styles), then the class-v2 overrides.
  styleUrls: [
    './../../assets/css/theme-default/bootstrap.css',
    './../../assets/css/theme-default/materialadmin.css',
    './../../assets/css/theme-default/font-awesome.min.css',
    './../../assets/css/theme-default/libs/bootstrap-datepicker/datepicker3.css',
    './../../assets/css/theme-default/libs/DataTables/jquery.dataTables.css',
    './../../assets/css/theme-default/libs/DataTables/extensions/dataTables.colVis.css',
    './../../assets/css/theme-default/material-design-iconic-font.min.css',
    './../../assets/css/theme-default/libs/DataTables/extensions/dataTables.tableTools.css',
    './../../assets/css/theme-default/libs/bootstrap-tagsinput/bootstrap-tagsinput.css',
    './../../assets/css/theme-default/libs/nestable/nestable.css',
    './../../assets/css/theme-default/libs/jquery-ui/jquery-ui-theme.css',
    './../../css/timepicker.css',
    './../../_crop/css/style.css',
    './../../_crop/css/style-example.css',
    './../classmanage/classmanage.component.css',
    './../feesmanage-v2/feesmanage-v2.component.css',
    './classmanage-v2.component.css',
  ],
  encapsulation: ViewEncapsulation.None,
  providers: [
    LoginService, FormValidator, Offcanvas, ScriptLoader, ClassLoadTracker,
    // The inherited code gets these two services through DI; handing it
    // tracked wrappers lets the template show per-list skeleton loaders.
    { provide: ClassManageService, useFactory: (http: HttpClient, t: ClassLoadTracker) => trackService(new ClassManageService(http), t), deps: [HttpClient, ClassLoadTracker] },
    { provide: GroupService, useFactory: (http: HttpClient, c: commonFunctionService, t: ClassLoadTracker) => trackService(new GroupService(http, c), t), deps: [HttpClient, commonFunctionService, ClassLoadTracker] },
  ],
})
export class ClassManageV2Component extends ClassmanageComponent implements AfterViewInit, AfterViewChecked, OnDestroy {
  // Client-side search / sort / paging for the list tables (replaces the old
  // jQuery DataTables, which cannot coexist with Angular-rendered rows).
  catTable = new TvTable(['CategoryName', 'TotalClasses', 'TotalSections', 'TotalStudent'], 'CategoryName');
  classTable = new TvTable(['ClassName', 'TotalSection', 'categoryName', 'TotalStudent', 'TotalStaff'], 'ClassName');
  subjectTable = new TvTable(['subject', 'belongingClasses'], 'subject');
  catClassTable = new TvTable(['ClassName', 'TotalSection', 'TotalStudent'], 'ClassName');
  studentTable = new TvTable(['studentName', 'rollNumber', 'parentName', 'mobileNumber'], 'studentName');

  private resizeListener = () => this.placeSlideIndicators();
  private loadTracker = inject(ClassLoadTracker);
  private skelCache = new Map<number, number[]>();

  /** True while any of the named service calls is in flight (drives the skeletons). */
  loading(...keys: string[]): boolean {
    return this.loadTracker.isLoading(keys);
  }

  /** Stable [0..n) array for *ngFor skeleton placeholders. */
  skel(n: number): number[] {
    let a = this.skelCache.get(n);
    if (!a) {
      a = Array.from({ length: n }, (_, i) => i);
      this.skelCache.set(n, a);
    }
    return a;
  }

  /** Re-sync a table with its (reassigned or resized) source array and return the visible page. */
  view(table: TvTable, source: any[]): any[] {
    const list = source || [];
    const t = table as any;
    if (table.rows !== list || t._n !== list.length) {
      t._n = list.length;
      table.setRows(list);
    }
    return table.pageRows;
  }

  // The inherited code calls these to (re)build jQuery DataTables on tables
  // that v2 renders and pages with Angular instead — so they are no-ops here.
  initializeDataTable(_table: string): void {}
  destroyDataTable(_table: string): void {}

  setActive(tab: string): void {
    super.setActive(tab);
    setTimeout(() => this.placeSlideIndicators());
  }

  ngAfterViewInit(): void {
    this.placeSlideIndicators();
    // Timetable cells are built by the inherited jQuery and their dropdowns clip
    // long names (and ignore the pointer in read-only mode), so show the full
    // subject / teacher name as a tooltip for whichever dropdown the cursor is over.
    $('body').on('mousemove.cv2tt', '#timetable td[class^=disableDay]', (ev: any) => {
      const td = ev.currentTarget as HTMLElement;
      let tip = '';
      td.querySelectorAll('select').forEach((sel: HTMLSelectElement) => {
        const r = sel.getBoundingClientRect();
        if (ev.clientX >= r.left && ev.clientX <= r.right && ev.clientY >= r.top && ev.clientY <= r.bottom) {
          const o = sel.selectedOptions && sel.selectedOptions[0];
          tip = o && o.value !== 'none' ? o.text : '';
        }
      });
      if (td.title !== tip) {
        td.title = tip;
      }
    });
    window.addEventListener('resize', this.resizeListener);
    (document as any).fonts?.ready?.then(() => this.placeSlideIndicators());
  }

  // Tabs are permission-gated and appear after the permissions load, so keep
  // the highlight aligned whenever the view settles (cheap: writes only on change).
  ngAfterViewChecked(): void {
    this.placeSlideIndicators();
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.resizeListener);
    $('body').off('mousemove.cv2tt');
    // The inherited ngOnInit attaches delegated handlers on <body> that are never
    // removed, so every visit (or a hop between the old and new class pages)
    // would stack another copy that acts on a destroyed instance. Drop them here.
    $('body')
      .off('change', '.timetable_subjects')
      .off('change', '.timetable_teachers')
      .off('click', '#selectCategory')
      .off('click', '.copy-timetable-slot')
      .off('click', '.paste-timetable-slot');
  }

  // Sliding highlight under the active main tab (same behaviour as fees-v2).
  private placeSlideIndicators(): void {
    document.querySelectorAll<HTMLElement>('.cv2-slider').forEach((track) => {
      const indicator = track.querySelector<HTMLElement>('.cv2-slide-indicator');
      const active = track.querySelector<HTMLElement>('.fv2-tab.active');
      if (!indicator) {
        return;
      }
      if (!active) {
        indicator.style.width = '0';
        return;
      }
      const transform = `translateX(${active.offsetLeft}px)`;
      const width = `${active.offsetWidth}px`;
      if (indicator.style.transform !== transform) {
        indicator.style.transform = transform;
      }
      if (indicator.style.width !== width) {
        indicator.style.width = width;
      }
    });
  }
}
